/**
 * Reset password — unauthenticated page for email link (?uid=…&token=…)
 */

import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "@weco/ui";
import { peopleAuthUrl } from "../config/peopleApi";

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid") ?? "";
  const token = searchParams.get("token") ?? "";

  const linkValid = useMemo(() => Boolean(uid && token), [uid, token]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!linkValid) {
      setError(t("resetPassword.errInvalidLink"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("resetPassword.errMismatch"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(peopleAuthUrl("password-reset/confirm/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, token, new_password: newPassword }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const detail = data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: { string?: string }) => d?.string || JSON.stringify(d)).join(" ")
              : data?.new_password
                ? Array.isArray(data.new_password)
                  ? data.new_password.join(" ")
                  : String(data.new_password)
                : t("resetPassword.errFailed");
        throw new Error(msg);
      }

      setSuccess(true);
    } catch (err: unknown) {
      const anyErr = err as { name?: string; message?: string };
      if (anyErr?.name === "TypeError" && String(anyErr?.message).includes("fetch")) {
        setError(
          "Cannot reach the People API. Start backends (bash scripts/run-backend.sh) and use the shell on the active Vite URL."
        );
      } else {
        setError(anyErr.message || t("resetPassword.errFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!linkValid) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-8"
        style={{ background: "var(--bg-root)", fontFamily: "Inter, sans-serif" }}
      >
        <div className="w-full max-w-sm text-center">
          <div className="mb-6 flex justify-center">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg"
              style={{ background: "#3366FF", color: "#FFFFFF" }}
            >
              W
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>
            {t("resetPassword.invalidTitle")}
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            {t("resetPassword.invalidBody")}
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full py-3 rounded-xl text-sm font-bold"
            style={{ background: "#3366FF", color: "#FFFFFF", boxShadow: "0 8px 32px rgba(var(--accent-rgb),0.3)" }}
          >
            {t("resetPassword.backToSignIn")}
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-8"
        style={{ background: "var(--bg-root)", fontFamily: "Inter, sans-serif" }}
      >
        <div className="w-full max-w-sm text-center">
          <div className="mb-6 flex justify-center">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg"
              style={{ background: "#3366FF", color: "#FFFFFF" }}
            >
              W
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>
            {t("resetPassword.successTitle")}
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            {t("resetPassword.successBody")}
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full py-3 rounded-xl text-sm font-bold"
            style={{ background: "#3366FF", color: "#FFFFFF", boxShadow: "0 8px 32px rgba(var(--accent-rgb),0.3)" }}
          >
            {t("resetPassword.backToSignIn")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-8"
      style={{ background: "var(--bg-root)", fontFamily: "Inter, sans-serif" }}
    >
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg shrink-0"
            style={{ background: "#3366FF", color: "#FFFFFF" }}
          >
            W
          </div>
          <div>
            <span className="inline-flex items-baseline gap-1.5">
              <span className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>
                We-Co Suite
              </span>
              <span
                className="text-[10px] font-bold tracking-widest uppercase"
                style={{ color: "var(--accent)" }}
              >
                {t("shell.betaLabel")}
              </span>
            </span>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {t("resetPassword.subtitle")}
            </p>
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>
          {t("resetPassword.heading")}
        </h1>
        <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
          {t("resetPassword.body")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
              {t("resetPassword.newPasswordLabel")}
            </label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t("resetPassword.newPasswordLabel")}
              required
              minLength={8}
              autoComplete="new-password"
              className="rounded-xl py-2.5"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
              {t("resetPassword.confirmPasswordLabel")}
            </label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t("resetPassword.confirmPasswordLabel")}
              required
              minLength={8}
              autoComplete="new-password"
              className="rounded-xl py-2.5"
            />
          </div>

          {error && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-bold transition-all duration-200"
            style={{
              background: loading ? "var(--bg-surface-3)" : "#3366FF",
              color: loading ? "var(--text-secondary)" : "#FFFFFF",
              border: "1px solid transparent",
              boxShadow: loading ? "none" : "0 8px 32px rgba(var(--accent-rgb),0.3)",
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                (e.currentTarget as HTMLElement).style.boxShadow = "0 12px 40px rgba(var(--accent-rgb),0.45)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 32px rgba(var(--accent-rgb),0.3)";
              (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
            }}
          >
            {loading ? (
              <>
                <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                {t("resetPassword.updating")}
              </>
            ) : (
              t("resetPassword.submit")
            )}
          </button>
        </form>

        <p className="text-center text-xs mt-8" style={{ color: "var(--text-muted)" }}>
          <Link to="/" className="underline hover:opacity-80" style={{ color: "var(--accent)" }}>
            {t("resetPassword.backToSignIn")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
